import {
  log,
  isSameContent,
  distantPast,
  localeCompare,
} from '@nx-superprep/utils'

import { DayPlanModule, StudyPhase, StudySetting, Constants, DeckMode, SpacedRepModuleConfig, isRegularStudyPhase, isQuizStudyPhase } from '../../back-end'
import { FrontendCard as Card, getRandomIndex, shuffleCards } from '../card'
import { uniqWith } from 'lodash-es'

const fixedFactorForProgress = Constants.MATURITY_POINT_CAP_FOR_PROGRESS

export class Deck {
  lastAnsweredCardId: string | undefined = undefined;

  setting!: StudySetting
  cards: Card[] = []
  card?: Card
  previousSelectedCard?: Card
  dayPlanModule!: DayPlanModule
  spacedRepConfig!: SpacedRepModuleConfig

  // current study phase
  phase = StudyPhase.Review
  // This index will only be used on brush up mode
  private _indexForCurrentCardForPractice = 0
  private _indexForCurrentCardForReview = 0
  private _indexForCurrentCardForQuiz = 0
  // Today
  private _reviewDeckToday: Card[] = []
  private _acquisitionDeckToday: Card[] = []
  //
  acquiredIdsToday: string[] = []
  //
  maturityProgress = 0

  // end of study cases

  // EOS from completion marker
  get isFinishedForToday() {
    return this.dayPlanModule.isFinished
  }
  // EOS w/completion reached during study
  get isFinishedForStudy() {
    return this.dayPlanModule.isFinished || this.dayPlanModule.progress >= 1
  }
  // EOS w/o completion reached during study
  get isCompleteForStudy() {
    return this.dayPlanModule.acquisitionProgress >= 1 && this.dayPlanModule.reviewProgress >= 1
  }

  // helper for autoplay
  get isLastCardForToday() {
    return this.isEndOfDeckForPhase(StudyPhase.Review) && (this.dailyStudiedProgress + 1) >=  this.dailyStudiedTarget
  }

  get isLastPhaseForToday() {
    return this.isFinishedForToday || !this.nextPhase || (this.nextPhase === this.phase)
  }

  get nextPhase() {
    return this.phase === StudyPhase.Review ? StudyPhase.Acquisition : undefined
  }

  get indexForCurrentCardForPractice() { return this._indexForCurrentCardForPractice }
  get indexForCurrentCardForReview() { return this._indexForCurrentCardForReview }
  set indexForCurrentCardForReview(value) { this._indexForCurrentCardForReview = value }
  get indexForCurrentCardForQuiz() { return this._indexForCurrentCardForQuiz }

  /**
   * Resets all card indexes to valid positions within their respective decks
   * @returns boolean indicating if any indexes were reset
   */
  resetIndexes(): boolean {
    let wasReset = false;

    // Reset review index
    if (this.phase === StudyPhase.Review && this.reviewCards.length > 0) {
      const maxReviewIndex = this.reviewCards.length - 1;
      if (this._indexForCurrentCardForReview > maxReviewIndex) {
        this._indexForCurrentCardForReview = Math.min(
          this.dayPlanModule.reviewProgress, // Don't go beyond progress
          maxReviewIndex
        );
        wasReset = true;
        log.debug(`Reset review index to ${this._indexForCurrentCardForReview}`);
      }
    }

    // Reset acquisition index (always 0 since acquisition uses first card)
    if (this.phase === StudyPhase.Acquisition && this.acquisitionCards.length > 0) {
      if (this._indexForCurrentCardForReview !== 0) { // Just for consistency
        this._indexForCurrentCardForReview = 0;
        wasReset = true;
      }
    }

    // Reset practice index
    if (this.phase === StudyPhase.Practice && this.cards.length > 0) {
      const maxPracticeIndex = this.cards.length - 1;
      if (this._indexForCurrentCardForPractice > maxPracticeIndex) {
        this._indexForCurrentCardForPractice = maxPracticeIndex;
        wasReset = true;
        log.debug(`Reset practice index to ${this._indexForCurrentCardForPractice}`);
      }
    }

    // Reset quiz index
    if (this.phase === StudyPhase.Quiz && this.cards.length > 0) {
      const maxQuizIndex = this.cards.length - 1;
      if (this._indexForCurrentCardForQuiz > maxQuizIndex) {
        this._indexForCurrentCardForQuiz = maxQuizIndex;
        wasReset = true;
        log.debug(`Reset quiz index to ${this._indexForCurrentCardForQuiz}`);
      }
    }

    return wasReset;
  }

  constructor(options: {
    studyPhase?: StudyPhase,
    setting: StudySetting,
    dayPlanModule: DayPlanModule,
    spacedRepConfig: SpacedRepModuleConfig,
    cards: Card[],
    reviewIds?: string[],
    lastAnswerCardId?: string
  }) {
    if (!options.setting || !options.dayPlanModule || !options.spacedRepConfig || !options.cards) { return }
    this._init(options)
  }

  static isReady(deck: Deck) {
    return Boolean(deck.name.length > 0)
  }

  isEqual(deck: Deck|undefined) {
    return deck
    && deck.dayPlanModule.moduleId === this.dayPlanModule.moduleId
    && deck.phase === this.phase
    && deck.targetLength === this.targetLength
    && deck.lastAnsweredCardId === this.lastAnsweredCardId
    && isSameContent(deck.currentCardIds(), this.currentCardIds())
  }

  isEndOfDeckForPhase(phase?: StudyPhase): boolean {
    const targetPhase = phase ?? this.phase;
    switch (targetPhase) {
      case StudyPhase.Review: return this.isEndOfStudyForReview || this.reviewCards.length === 0;
      case StudyPhase.Acquisition: return this.isEndOfStudyForAcquisition || this.acquisitionCards.length === 0;
      case StudyPhase.Practice:
      case StudyPhase.Quiz:
      default: return false;
    }
  }

  get isInReview() { return this.phase === StudyPhase.Review }
  get isInAcquisition() { return this.phase === StudyPhase.Acquisition }
  get isInPractice() { return this.phase === StudyPhase.Practice }
  get isInQuiz() { return this.phase === StudyPhase.Quiz }

  get isDepleted() { return this.isInAcquisition && (this.acquisitionCards.length > 0 && this.acquisitionCards.every(c => c.inReview)) }
  get isRegularStudyComplete(): boolean { return this.isEndOfStudyForReview && (this.dayPlanModule.isAcquisitionFinished || this.isEndOfStudyForAcquisition) }
  get shouldTransitionToAcquisition(): boolean { return this.phase === StudyPhase.Review && !this.dayPlanModule.isAcquisitionFinished && this.isEndOfStudyForReview }
  get shouldEndStudy(): boolean {
    return this.dayPlanModule.isFinished
      || (this.phase === StudyPhase.Review && this.isEndOfStudyForReview && this.dayPlanModule.isAcquisitionFinished)
      || (this.phase === StudyPhase.Acquisition && this.isEndOfStudyForAcquisition);
  }

  switchToAcquisition() {
    this.clear(StudyPhase.Acquisition)
  }

  clear(phase = StudyPhase.Review) {
    this.card = undefined
    this.cards = []
    this._reviewDeckToday = []
    this._acquisitionDeckToday = []
    this._indexForCurrentCardForPractice = 0
    this._indexForCurrentCardForReview = 0
    this._indexForCurrentCardForQuiz = 0
    this.phase = phase
  }

  currentCards(phase: StudyPhase = this.phase) {
    switch (phase) {
      case StudyPhase.Review: return this.reviewCards
      case StudyPhase.Acquisition: return this.acquisitionCards
      case StudyPhase.Practice:
      case StudyPhase.Quiz:
      default: return this.cards
    }
  }

  currentCardIds(sorted = true, phase: StudyPhase = this.phase) {
    const cardIds = this.currentCards(phase).map(c => c.id)
    return sorted ? cardIds.sort((a,b) => localeCompare(a,b)) : cardIds
  }

  private _init(options: {
    acquiredIdsToday?: string[],
    studyPhase?: StudyPhase,
    setting: StudySetting,
    dayPlanModule: DayPlanModule,
    spacedRepConfig: SpacedRepModuleConfig,
    cards: Card[],
    reviewIds?: string[],
    isUpdate?: boolean,
    indexForCurrentCardForPractice?: number,
    indexForCurrentCardForReview?: number,
    indexForCurrentCardForQuiz?: number,
    lastAnsweredCardId?: string
  }) {
    // Clear last answered card if transitioning phases
    if (options.isUpdate && this.phase !== options.studyPhase) {
        options.lastAnsweredCardId = undefined;
    }
    // last answered card id
    this.lastAnsweredCardId = options.lastAnsweredCardId
    // study phase
    this.phase = options.studyPhase ?? StudyPhase.Review
    // setting
    this.setting = options.setting
    // day Plan Module
    this.dayPlanModule = options.dayPlanModule
    //
    this.spacedRepConfig = options.spacedRepConfig
    //
    this.acquiredIdsToday = options.acquiredIdsToday ?? []
    // indexes for review/practice
    this._indexForCurrentCardForPractice = Number(options.indexForCurrentCardForPractice) || 0
    this._indexForCurrentCardForReview = Number(options.indexForCurrentCardForReview) || 0
    this._indexForCurrentCardForQuiz = Number(options.indexForCurrentCardForQuiz) || 0
    // generator logic requires that cards be in id ascending order
    this.cards = uniqWith(options.cards, Card.isEqualId)

    switch (this.phase) {
      case StudyPhase.Review: {
        this._acquisitionDeckToday = []
        this._reviewDeckToday = options.isUpdate ? this.cards.slice().filter(c => c.id !== options.lastAnsweredCardId) : shuffleCards(this.cards, this.phase)
      } break;
      case StudyPhase.Acquisition: {
        this._acquisitionDeckToday = (options.isUpdate ? this.cards : shuffleCards(this.cards, this.phase)).slice(0, this.targetLength);
        this._reviewDeckToday = [];
      } break;
      default: {
        this.cards = (options.isUpdate ? this.cards : shuffleCards(this.cards, this.phase)).slice(0, this.targetLength);
      }
    }
  }

  private get dailyStudiedProgress(): number {
    return this.dayPlanModule.acquisitionProgress + this.dayPlanModule.reviewProgress
  }

  private get dailyStudiedTarget(): number {
    return this.dayPlanModule.acquisitionTarget + this.dayPlanModule.reviewTarget
  }

  // helpers

  get progress() {
    return isRegularStudyPhase(this.phase)
    ? this.dayPlanModule.progress
    : isQuizStudyPhase(this.phase)
      ? this._indexForCurrentCardForQuiz / (this.cards.length ?? 1)
      : this._indexForCurrentCardForPractice / (this.cards.length ?? 1)
  }

  get mode() {
    return this.spacedRepConfig.deckMode
  }

  get toTargetLength() {
    return Math.abs(this.targetLength - this.currentLength)
  }

  get targetLength() {
    switch (this.phase) {
      case StudyPhase.Review: return this.dayPlanModule.reviewTarget - this.dayPlanModule.reviewProgress
      case StudyPhase.Acquisition: return this.spacedRepConfig.deckLength[this.difficulty]
      case StudyPhase.Practice:
      case StudyPhase.Quiz:
      default: return 10
    }
  }

  get currentLength() {
    return this.currentCards().length
  }

  get reviewCards() {
    return this._reviewDeckToday
  }

  get acquisitionCards() {
    return this._acquisitionDeckToday
  }

  get reviewCardTodayCount() {
    return this.reviewCards.length ?? 0
  }

  get studyProgress() {
    return this.dayPlanModule.progress
  }

  // custom end of study phase evaluators
  get isEndOfStudyForAcquisition() {
    return this.dayPlanModule.isFinished || (this.dayPlanModule.acquisitionProgress >= this.dayPlanModule.acquisitionTarget) //|| this.isDepleted
  }
  get isEndOfStudyForReview() {
    // Check both progress AND actual cards remaining
    return this.dayPlanModule.isFinished ||
      (this.dayPlanModule.reviewProgress >= this.dayPlanModule.reviewTarget &&
       this._indexForCurrentCardForReview >= this.reviewCards.length - 1)
  }


  increaseIndex(phase = this.phase) {
    switch(phase) {
      case StudyPhase.Practice: this._indexForCurrentCardForPractice += 1; break
      case StudyPhase.Review: this._indexForCurrentCardForReview += 1; break
      case StudyPhase.Quiz: this._indexForCurrentCardForQuiz += 1; break
      case StudyPhase.Acquisition:
      default: break
    }
  }

  get workflow(): DeckMode {
    return this.setting ? this.spacedRepConfig.deckMode : DeckMode.Wide
  }

  get duration() {
    return this.setting.duration
  }

  get difficulty() {
    return this.setting.difficulty
  }

  // accessors for properties of lessson

  get lastStudyDate() {
    return this.dayPlanModule?.lastStudyDate ?? distantPast
  }

  get name() {
    return this.dayPlanModule.moduleId
  }

  get displayName() {
    return this.dayPlanModule.moduleTitle
  }

  get isLoaded() {
    switch (this.phase) {
      case StudyPhase.Acquisition: return this.acquisitionCards.length >= this.targetLength
      case StudyPhase.Review: return this.reviewCardTodayCount > 0
      case StudyPhase.Practice:
      case StudyPhase.Quiz:
      default: return this.cards.length > 0
    }
  }

  get lastReviewCard() {
    return this.reviewCards.slice(-1)[0]
  }

  getCurrentCard(phase: StudyPhase = this.phase): Card|undefined {
    const wasReset = this.resetIndexes(); // ensure indexes are valid
    switch (phase) {
      case StudyPhase.Acquisition:
        return (this.isEndOfStudyForAcquisition && this.progress >= 1) ? undefined : this._acquisitionDeckToday[0];
      case StudyPhase.Review:
        return this._indexForCurrentCardForReview >= this.reviewCards.length ? undefined : this._reviewDeckToday[this._indexForCurrentCardForReview];
      case StudyPhase.Practice:
        return wasReset ? undefined : this.cards[this._indexForCurrentCardForPractice];
      case StudyPhase.Quiz:
        return wasReset ? undefined : this.cards[this._indexForCurrentCardForQuiz];
      default:
        return undefined;
    }
  }

  repositionCard(
    card: Card,
    isCorrect: boolean,
    spacedRepConfig = this.spacedRepConfig,
  ) {
    // only reposition in acquisition phase
    if (this.phase !== StudyPhase.Acquisition) { return -1 }
    const index = this._acquisitionDeckToday.findIndex((c) => c.isSameName(card))
    if (index < 0) { return -1 }
    this._acquisitionDeckToday.splice(index, 1)
    if (!card.inReview) {
      const randomIndex = getRandomIndex(isCorrect, this.setting, spacedRepConfig)
      this._acquisitionDeckToday.splice(randomIndex, 0, card)
      log.debug(`repositioned ${card.id} at ${randomIndex}`)
    } else if (card.justAcquired){
      log.debug(`acquired card: ${card.id}`)
      this.acquiredIdsToday.push(card.id)
    }
    return this._acquisitionDeckToday.findIndex((c) => c.isSameName(card))
  }

  update() {
    if (!this.setting) { return; }
    // update maturity progress
    const reducer = (total: number, c: Card) => total + Math.min(c.maturityPoints, fixedFactorForProgress)
    const totalMaturityPoints = this.cards.reduce(reducer, 0)
    this.maturityProgress = totalMaturityPoints / (this.cards.length * fixedFactorForProgress)
  }
}
